import React, { useEffect, useState } from "react";
import axios from "axios";
import { Quote, MessageSquareHeart, ArrowRight, Plus, Star, X, Loader2, Image as ImageIcon, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// English translations for approved testimonials (keyed by testimonial id).
// Add new entries here whenever a Romanian review is approved so the EN
// language version shows a proper translation.
const TESTIMONIAL_EN = {
  "7fc57fd9-cd9d-4ac7-995d-1d1480fc9ee0":
    "My heartfelt thanks to the legislation teacher, who dedicated his time to teach me with patience and devotion, and to the driving instructor — an extraordinary person who offered me understanding, calmness and the utmost patience.\n\nExcellent services, professionalism and, above all, people who put their soul into what they do. I wholeheartedly recommend this driving school! Thank you for everything!",
};

const Testimonials = () => {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then((r) => setItems(r.data || [])).catch(() => setItems([]));
    // API and axios are module-level constants; fetch runs once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 sm:py-32 bg-brand-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-3xl">
            <p className="text-xs sm:text-sm uppercase tracking-[0.22em] font-bold text-brand-orange mb-4">
              {t.testimonials.eyebrow}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-navy leading-tight tracking-tight">
              {t.testimonials.title}
            </h2>
            <p className="mt-5 text-base sm:text-lg text-brand-muted leading-relaxed">
              {t.testimonials.subtitle}
            </p>
          </div>
          <button
            onClick={() => { setSuccess(false); setOpen(true); }}
            data-testid="testimonials-add-btn"
            className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orange-deep text-white font-semibold px-6 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-md flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            {t.testimonials.addBtn}
          </button>
        </div>

        {items.length === 0 ? (
          <div
            data-testid="testimonials-empty"
            className="relative rounded-3xl border-2 border-dashed border-brand-border-soft bg-white p-10 sm:p-14 text-center overflow-hidden"
          >
            <div className="absolute -top-10 -left-10 w-48 h-48 rounded-full bg-brand-orange/10 blur-3xl" />
            <div className="absolute -bottom-10 -right-10 w-48 h-48 rounded-full bg-brand-navy/10 blur-3xl" />
            <div className="relative">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-orange/15 text-brand-orange-deep mb-6">
                <MessageSquareHeart className="w-8 h-8" />
              </div>
              <h3 className="font-display text-2xl sm:text-3xl font-bold text-brand-navy max-w-xl mx-auto">
                {t.testimonials.empty}
              </h3>
              <button
                onClick={scrollToContact}
                data-testid="testimonials-cta"
                className="mt-8 inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-navy-deep text-white font-semibold px-7 py-4 rounded-full transition-all hover:-translate-y-0.5"
              >
                {t.testimonials.cta}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((tst) => (
              <div key={tst.id} data-testid={`testimonial-${tst.id}`} className="bg-white border border-brand-border-soft rounded-3xl p-7 lift-on-hover">
                <div className="flex items-center gap-0.5 mb-3">
                  {[1,2,3,4,5].map((n) => (
                    <Star key={n} className={`w-4 h-4 ${n <= tst.rating ? "fill-brand-orange text-brand-orange" : "text-brand-border-soft"}`} />
                  ))}
                </div>
                <Quote className="w-6 h-6 text-brand-orange/30 mb-3" />
                <p className="text-brand-navy text-base leading-relaxed whitespace-pre-line">{(lang === "en" && TESTIMONIAL_EN[tst.id]) || tst.text}</p>
                {tst.photo_paths && tst.photo_paths.length > 0 && (
                  <div className="mt-4 flex gap-2 flex-wrap">
                    {tst.photo_paths.map((p) => (
                      <img
                        key={p}
                        src={`${API}/testimonials/photo/${p}`}
                        alt="testimonial"
                        className="w-20 h-20 rounded-xl object-cover border border-brand-border-soft"
                      />
                    ))}
                  </div>
                )}
                <div className="mt-6 pt-5 border-t border-brand-border-soft">
                  <p className="font-display font-bold text-brand-navy">{tst.name}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {open && (
        <TestimonialForm
          t={t}
          success={success}
          setSuccess={setSuccess}
          onClose={() => setOpen(false)}
        />
      )}
    </section>
  );
};

const TestimonialForm = ({ t, success, setSuccess, onClose }) => {
  const [form, setForm] = useState({ name: "", rating: 5, text: "" });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const onFileChange = (e) => {
    const list = Array.from(e.target.files || []).slice(0, 3);
    setFiles(list);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Upload photos first
      const photo_paths = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const up = await axios.post(`${API}/testimonials/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (up.data?.path) photo_paths.push(up.data.path);
      }
      await axios.post(`${API}/testimonials`, { ...form, photo_paths });
      setSuccess(true);
      toast.success(t.testimonials.form.successMsg);
    } catch (err) {
      toast.error(t.testimonials.form.errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="testimonial-form-modal"
      className="fixed inset-0 z-[100] bg-brand-navy-deep/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-3xl w-full max-w-lg my-8 shadow-2xl relative">
        <button
          onClick={onClose}
          data-testid="testimonial-form-close"
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-brand-cream hover:bg-brand-border-soft flex items-center justify-center transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-brand-navy" />
        </button>

        {success ? (
          <div className="p-10 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 text-green-600 mb-4">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <h3 className="font-display text-2xl font-bold text-brand-navy">{t.testimonials.form.successTitle}</h3>
            <p className="mt-3 text-brand-muted">{t.testimonials.form.successMsg}</p>
            <button
              onClick={onClose}
              className="mt-6 inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orange-deep text-white font-semibold px-6 py-3 rounded-full"
            >
              OK
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="p-7 sm:p-9">
            <h3 className="font-display text-2xl font-bold text-brand-navy">{t.testimonials.form.title}</h3>
            <p className="mt-2 text-sm text-brand-muted">{t.testimonials.form.subtitle}</p>

            <div className="mt-6 space-y-4">
              <label className="block">
                <span className="block text-xs uppercase tracking-wider font-bold text-brand-navy mb-2">{t.testimonials.form.name} *</span>
                <input
                  type="text"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="testimonial-name"
                  className="w-full px-4 py-3 rounded-xl border border-brand-border-soft bg-white text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-orange"
                />
              </label>

              <div>
                <span className="block text-xs uppercase tracking-wider font-bold text-brand-navy mb-2">{t.testimonials.form.rating} *</span>
                <div className="flex gap-1" data-testid="testimonial-rating">
                  {[1,2,3,4,5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, rating: n }))}
                      data-testid={`testimonial-star-${n}`}
                      className="p-1"
                    >
                      <Star className={`w-8 h-8 transition-colors ${n <= form.rating ? "fill-brand-orange text-brand-orange" : "text-brand-border-soft hover:text-brand-orange/50"}`} />
                    </button>
                  ))}
                </div>
              </div>

              <label className="block">
                <span className="block text-xs uppercase tracking-wider font-bold text-brand-navy mb-2">{t.testimonials.form.text} *</span>
                <textarea
                  required
                  minLength={10}
                  maxLength={1500}
                  rows={5}
                  placeholder={t.testimonials.form.textPlaceholder}
                  value={form.text}
                  onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                  data-testid="testimonial-text"
                  className="w-full px-4 py-3 rounded-xl border border-brand-border-soft bg-white text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-orange resize-none"
                />
              </label>

              <label className="block">
                <span className="block text-xs uppercase tracking-wider font-bold text-brand-navy mb-2">{t.testimonials.form.photos}</span>
                <div className="border-2 border-dashed border-brand-border-soft rounded-xl p-4 hover:border-brand-orange transition-colors">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={onFileChange}
                    data-testid="testimonial-photos"
                    className="w-full text-sm text-brand-muted file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-brand-orange/10 file:text-brand-orange-deep file:font-semibold hover:file:bg-brand-orange/20"
                  />
                  <p className="mt-2 text-xs text-brand-muted flex items-center gap-1.5">
                    <ImageIcon className="w-3.5 h-3.5" />
                    {t.testimonials.form.photosHint}
                  </p>
                  {files.length > 0 && (
                    <p className="mt-2 text-xs text-brand-orange-deep font-semibold">
                      {files.length} {files.length === 1 ? "fișier selectat" : "fișiere selectate"}
                    </p>
                  )}
                </div>
              </label>
            </div>

            <div className="mt-7 flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3.5 rounded-full border border-brand-border-soft text-brand-navy font-semibold hover:bg-brand-cream transition-colors"
              >
                {t.testimonials.form.cancel}
              </button>
              <button
                type="submit"
                disabled={submitting}
                data-testid="testimonial-submit"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-orange hover:bg-brand-orange-deep disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-full transition-all shadow-md"
              >
                {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" />{t.testimonials.form.submitting}</>) : t.testimonials.form.submit}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Testimonials;
